const clamp01 = (value) => Math.max(0, Math.min(1, value));
const round = (value, digits = 4) => Number(value.toFixed(digits));
/** Lightweight grid-based block matching for local, browser-friendly inter-frame motion estimates. */
export class OpticalFlowAnalyzer {
    id = 'optical-flow';
    label = 'Optical Flow / Motion';
    previous;
    previousMotion;
    maxWidth;
    maxHeight;
    gridStep;
    searchRadius;
    constructor(options = {}) {
        this.maxWidth = Math.max(32, Math.floor(options.maxWidth ?? 96));
        this.maxHeight = Math.max(24, Math.floor(options.maxHeight ?? 64));
        this.gridStep = Math.max(5, Math.floor(options.gridStep ?? 8));
        this.searchRadius = Math.max(2, Math.floor(options.searchRadius ?? 5));
    }
    reset() {
        this.previous = undefined;
        this.previousMotion = undefined;
    }
    analyze(frame, context) {
        const startedAt = performance.now();
        const current = this.downscale(frame);
        const previous = this.previous;
        this.previous = current;
        if (!previous || previous.width !== current.width || previous.height !== current.height) {
            this.previousMotion = undefined;
            return this.result(context, {
                motion_magnitude: 0,
                average_motion_x: 0,
                average_motion_y: 0,
                motion_direction: 'unknown',
                motion_stability: 0,
                shake_score: 0,
                possible_camera_movement: 'unknown',
                confidence: 0,
                processing_time_ms: 0,
                vectors: [],
            }, [{
                    code: 'insufficient_motion_history',
                    severity: 'info',
                    message: 'Insufficient texture for reliable motion estimation',
                    module_id: this.id,
                    frame_number: context.frameNumber,
                }], [], startedAt);
        }
        const blockRadius = 2;
        const vectors = [];
        let attemptedBlocks = 0;
        for (let y = blockRadius + this.searchRadius; y < current.height - blockRadius - this.searchRadius; y += this.gridStep) {
            for (let x = blockRadius + this.searchRadius; x < current.width - blockRadius - this.searchRadius; x += this.gridStep) {
                const texture = this.blockVariance(current.luma, current.width, x, y, blockRadius);
                if (texture < 22)
                    continue;
                attemptedBlocks++;
                let bestError = Number.POSITIVE_INFINITY;
                let secondError = Number.POSITIVE_INFINITY;
                let bestPreviousX = x;
                let bestPreviousY = y;
                for (let py = y - this.searchRadius; py <= y + this.searchRadius; py++) {
                    for (let px = x - this.searchRadius; px <= x + this.searchRadius; px++) {
                        const error = this.blockError(current.luma, previous.luma, current.width, x, y, px, py, blockRadius);
                        if (error < bestError) {
                            secondError = bestError;
                            bestError = error;
                            bestPreviousX = px;
                            bestPreviousY = py;
                        }
                        else if (error < secondError) {
                            secondError = error;
                        }
                    }
                }
                const uniqueness = secondError > 0
                    ? clamp01((secondError - bestError) / Math.max(8, secondError))
                    : bestError === 0 ? 1 : 0;
                const matchQuality = clamp01(1 - bestError / 1600);
                const confidence = clamp01(uniqueness * 0.65 + matchQuality * 0.35);
                if (confidence < 0.22)
                    continue;
                vectors.push({
                    x: round(x * current.scaleX, 2),
                    y: round(y * current.scaleY, 2),
                    dx: round((x - bestPreviousX) * current.scaleX, 2),
                    dy: round((y - bestPreviousY) * current.scaleY, 2),
                    confidence: round(confidence),
                });
            }
        }
        const weightSum = vectors.reduce((sum, vector) => sum + vector.confidence, 0);
        const averageX = weightSum
            ? vectors.reduce((sum, vector) => sum + vector.dx * vector.confidence, 0) / weightSum : 0;
        const averageY = weightSum
            ? vectors.reduce((sum, vector) => sum + vector.dy * vector.confidence, 0) / weightSum : 0;
        const magnitude = Math.hypot(averageX, averageY);
        const residual = vectors.length
            ? Math.sqrt(vectors.reduce((sum, vector) => sum + (vector.dx - averageX) ** 2 + (vector.dy - averageY) ** 2, 0) / vectors.length)
            : 0;
        const vectorMagnitude = vectors.length
            ? vectors.reduce((sum, vector) => sum + Math.hypot(vector.dx, vector.dy), 0) / vectors.length : 0;
        const consistency = vectors.length ? clamp01(1 - residual / Math.max(1.5, vectorMagnitude * 1.5)) : 0;
        let radialSum = 0;
        let radialWeight = 0;
        for (const vector of vectors) {
            const rx = vector.x - frame.width / 2;
            const ry = vector.y - frame.height / 2;
            const radius = Math.hypot(rx, ry);
            const vectorLength = Math.hypot(vector.dx, vector.dy);
            if (radius < Math.min(frame.width, frame.height) * 0.12 || vectorLength < 0.2)
                continue;
            radialSum += ((vector.dx * rx + vector.dy * ry) / (radius * vectorLength)) * vector.confidence;
            radialWeight += vector.confidence;
        }
        const radialDirection = radialWeight ? radialSum / radialWeight : 0;
        const temporalChange = this.previousMotion
            ? Math.hypot(averageX - this.previousMotion.x, averageY - this.previousMotion.y) : 0;
        const directionReversal = this.previousMotion &&
            Math.hypot(this.previousMotion.x, this.previousMotion.y) > 1 &&
            magnitude > 1 &&
            this.previousMotion.x * averageX + this.previousMotion.y * averageY < 0;
        const shakeScore = clamp01((1 - consistency) * 0.35 +
            Math.min(1, temporalChange / Math.max(2, magnitude + 1)) * 0.45 +
            (directionReversal ? 0.25 : 0) +
            (vectors.length && magnitude < 1 && vectorMagnitude > 1.5 ? 0.1 : 0));
        this.previousMotion = { x: averageX, y: averageY };
        const stability = clamp01(1 - shakeScore * 0.65 - (1 - consistency) * 0.35);
        const textureCoverage = clamp01(vectors.length / Math.max(6, attemptedBlocks));
        const meanConfidence = vectors.length
            ? vectors.reduce((sum, vector) => sum + vector.confidence, 0) / vectors.length : 0;
        const confidence = clamp01(textureCoverage * 0.45 + meanConfidence * 0.3 + consistency * 0.25);
        const radialMotion = vectorMagnitude > 0.7 && Math.abs(radialDirection) > 0.48;
        let direction = 'unknown';
        let cameraMovement = 'unknown';
        if (vectors.length < 3 || confidence < 0.32) {
            direction = 'unknown';
            cameraMovement = 'unknown';
        }
        else if (magnitude < 0.35 && vectorMagnitude < 0.65) {
            direction = 'static';
            cameraMovement = 'static';
        }
        else if (radialMotion) {
            direction = radialDirection > 0 ? 'forward' : 'backward';
            cameraMovement = radialDirection > 0 ? 'push_in' : 'pull_out';
        }
        else if (shakeScore >= 0.58) {
            direction = 'mixed';
            cameraMovement = 'shake';
        }
        else if (stability < 0.58 && magnitude < 2.5) {
            direction = 'mixed';
            cameraMovement = 'handheld';
        }
        else if (Math.abs(averageX) >= Math.abs(averageY) * 1.25) {
            direction = averageX < 0 ? 'left' : 'right';
            cameraMovement = averageX < 0 ? 'pan_left' : 'pan_right';
        }
        else if (Math.abs(averageY) >= Math.abs(averageX) * 1.25) {
            direction = averageY < 0 ? 'up' : 'down';
            cameraMovement = averageY < 0 ? 'tilt_up' : 'tilt_down';
        }
        else {
            direction = 'mixed';
            cameraMovement = stability < 0.72 ? 'handheld' : 'unknown';
        }
        const warnings = [];
        const warning = (code, message) => {
            warnings.push({ code, severity: 'warning', message, module_id: this.id, frame_number: context.frameNumber });
        };
        if (vectors.length < 4 || confidence < 0.28)
            warning('insufficient_motion_texture', 'Insufficient texture for reliable motion estimation');
        if (shakeScore >= 0.58)
            warning('camera_shake', 'Camera shake detected');
        if (vectorMagnitude >= 4.5) {
            warning('fast_movement', 'Fast movement detected');
            warning('motion_blur_key_risk', 'Motion blur may affect key quality');
        }
        if (stability < 0.48 && vectors.length >= 4)
            warning('unstable_motion', 'Motion is unstable');
        const overlays = vectors.slice(0, 80).map((vector) => ({
            type: 'vector_field',
            points: [
                { x: vector.x / frame.width, y: vector.y / frame.height },
                { x: (vector.x + vector.dx * 3) / frame.width, y: (vector.y + vector.dy * 3) / frame.height },
            ],
            label: 'Motion vector',
            color: vector.confidence >= 0.55 ? '#62f6ff' : '#ffb454',
            opacity: 0.72,
            stroke_width: 1,
        }));
        if (magnitude >= 0.25) {
            overlays.push({
                type: 'arrow',
                points: [
                    { x: 0.5, y: 0.5 },
                    { x: clamp01(0.5 + averageX * 0.025), y: clamp01(0.5 + averageY * 0.025) },
                ],
                label: 'Dominant motion',
                color: '#dfff57',
                opacity: 1,
                stroke_width: 4,
            });
        }
        vectors.filter((vector) => Math.hypot(vector.dx - averageX, vector.dy - averageY) > Math.max(2, residual * 1.35))
            .slice(0, 12)
            .forEach((vector) => overlays.push({
            type: 'box',
            points: [
                { x: clamp01(vector.x / frame.width - 0.035), y: clamp01(vector.y / frame.height - 0.035) },
                { x: clamp01(vector.x / frame.width + 0.035), y: clamp01(vector.y / frame.height + 0.035) },
            ],
            label: 'Unstable motion region',
            color: '#ff6b7a',
            opacity: 0.55,
            stroke_width: 2,
        }));
        if (shakeScore >= 0.58) {
            overlays.push({
                type: 'text',
                points: [{ x: 0.03, y: 0.08 }],
                label: 'CAMERA SHAKE',
                color: '#ff6b7a',
                opacity: 1,
            });
        }
        return this.result(context, {
            motion_magnitude: round(magnitude, 3),
            average_motion_x: round(averageX, 3),
            average_motion_y: round(averageY, 3),
            motion_direction: direction,
            motion_stability: round(stability),
            shake_score: round(shakeScore),
            possible_camera_movement: cameraMovement,
            confidence: round(confidence),
            processing_time_ms: 0,
            vectors,
        }, warnings, overlays, startedAt);
    }
    downscale(frame) {
        const scale = Math.min(1, this.maxWidth / frame.width, this.maxHeight / frame.height);
        const width = Math.max(1, Math.round(frame.width * scale));
        const height = Math.max(1, Math.round(frame.height * scale));
        const luma = new Float32Array(width * height);
        for (let y = 0; y < height; y++) {
            const sourceY = Math.min(frame.height - 1, Math.floor((y + 0.5) / scale));
            for (let x = 0; x < width; x++) {
                const sourceX = Math.min(frame.width - 1, Math.floor((x + 0.5) / scale));
                const offset = (sourceY * frame.width + sourceX) * 4;
                luma[y * width + x] =
                    0.2126 * (frame.data[offset] ?? 0) +
                        0.7152 * (frame.data[offset + 1] ?? 0) +
                        0.0722 * (frame.data[offset + 2] ?? 0);
            }
        }
        return { width, height, scaleX: frame.width / width, scaleY: frame.height / height, luma };
    }
    blockVariance(data, width, cx, cy, radius) {
        let sum = 0;
        let sumSquared = 0;
        let count = 0;
        for (let y = cy - radius; y <= cy + radius; y++) {
            for (let x = cx - radius; x <= cx + radius; x++) {
                const value = data[y * width + x] ?? 0;
                sum += value;
                sumSquared += value * value;
                count++;
            }
        }
        return Math.max(0, sumSquared / count - (sum / count) ** 2);
    }
    blockError(current, previous, width, currentX, currentY, previousX, previousY, radius) {
        let error = 0;
        let count = 0;
        for (let dy = -radius; dy <= radius; dy++) {
            for (let dx = -radius; dx <= radius; dx++) {
                error += Math.abs((current[(currentY + dy) * width + currentX + dx] ?? 0) -
                    (previous[(previousY + dy) * width + previousX + dx] ?? 0));
                count++;
            }
        }
        return error / count;
    }
    result(context, metrics, warnings, overlays, startedAt) {
        const completedMetrics = {
            ...metrics,
            processing_time_ms: round(performance.now() - startedAt, 3),
        };
        return {
            module_id: this.id,
            module_name: this.label,
            confidence: completedMetrics.confidence,
            frame_number: context.frameNumber,
            timestamp_ms: context.timestampMs,
            metrics: completedMetrics,
            warnings,
            overlays,
        };
    }
}
//# sourceMappingURL=OpticalFlowAnalyzer.js.map